import { Formula, Product, ProductForFe } from "models/product.types";
import { customSecuredApi } from ".";
import Config from "../Config";
import ErrorAPI from "../helpers/api";

export const defaultContractMonthDuration = 36;
export const defaultContractRental = true;
export const defaultContractRentalType = 'FLEX';

export const defaultPersonalizedModelId = 'generic_hardware';
export const defaultPersonalizedServiceModelId = 'generic_services';

export const getProductListApi = async ({
  agencyId,
  familyId,
  modelId,
  productIds,
}: {
  agencyId: string;
  familyId?: string;
  modelId?: string;
  productIds?: string[];
}) =>
  customSecuredApi.call({
    method: "GET",
    path: `${Config.apis.products.list.replace(":agencyId", agencyId)}?${
      familyId ? `productFamilyId=${familyId}` : ""
    }${modelId ? `&modelId=${modelId}` : ""}${productIds ? `&productId=${productIds.join('&productId=')}` : ""}`,
  });

export const getProductFamilyListApi = async (agencyId: string) =>
  customSecuredApi.call({
    method: "GET",
    path: `${Config.apis.products.families}?agencyId=${agencyId}&includeImage=true`,
  });

export const getFinancialDurationListApi = async (
  includeNonVisible: boolean = false,
  modelId: string | null | undefined = undefined,
  formula: Formula | null | undefined = undefined,
) => {
  const queryParams = new URLSearchParams();
  if (includeNonVisible) queryParams.append("includeNonVisible", "true");
  if (modelId) queryParams.append("modelId", modelId);
  if (formula) queryParams.append("formula", formula);
  const queryParamsString = queryParams.toString();
  return customSecuredApi.call({
    method: "GET",
    path: `${Config.apis.products.duration}${queryParamsString ? `?${queryParamsString}` : ""}`,
  });
}

export const getAccessoryListApi = async ({
  agencyId,
  productModelId,
}: {
  agencyId: string;
  productModelId: string;
}) =>
  customSecuredApi.call({
    method: "GET",
    path: `${Config.apis.products.accessories
      .replace(":agencyId", agencyId)
      .replace(":productModelId", productModelId)}`,
  });

export const getServicesListApi = async ({
  agencyId,
  productModelId,
}: {
  agencyId: string;
  productModelId: string;
}) =>
  customSecuredApi.call({
    method: "GET",
    path: `${Config.apis.products.services
      .replace(":agencyId", agencyId)
      .replace(":productModelId", productModelId)}`,
  });

export const getPricesForCart = async ({
  agencyId,
  body,
}: {
  agencyId: string;
  body: any;
}) =>
  customSecuredApi.call({
    method: "POST",
    path: `${Config.apis.products.prices.replace(":agencyId", agencyId)}`,
    body: body,
  });

export const getPersonalizedProductPrice = async ({
  agencyId,
  params,
}: {
  agencyId: string;
  params: {
    financialDurationId: string;
    formula: Formula;
    supplierAndChargesPrice: number;
    genericModelId: string;
    creditProvider?: string;
  };
}) => {
  const queryParams = new URLSearchParams();
  queryParams.append("financialDurationId", params.financialDurationId);
  queryParams.append("formula", params.formula);
  queryParams.append("supplierAndChargesPrice", params.supplierAndChargesPrice.toString());
  queryParams.append("genericModelId", params.genericModelId);
  if (params.creditProvider) queryParams.append("creditProvider", params.creditProvider);

  const queryParamsString = queryParams.toString();

  const { payload, body: bodyApi } = await customSecuredApi.call({
    method: "POST",
    path: `${Config.apis.products.personalizedProductPrice.replace(":agencyId", agencyId)}${queryParamsString ? `?${queryParamsString}` : ""}`,
  });
  if (payload.ok) {
    return bodyApi;
  } else {
    throw new ErrorAPI(payload.status, bodyApi);
  }
};

export const getTaxSavings = async ({
  agencyId,
  body,
}: {
  agencyId: string;
  body: any;
}) =>
  customSecuredApi.call({
    method: "POST",
    path: `${Config.apis.products.taxSavings.replace(":agencyId", agencyId)}`,
    body: body,
  });

export const createContractApi = async ({
  body,
  agencyId,
}: {
  body: any;
  agencyId: string;
}) => {
  const { payload, body: bodyApi } = await customSecuredApi.call({
    method: "POST",
    path: `${Config.apis.products.createContract.replace(
      ":agencyId",
      agencyId,
    )}`,
    body: body,
  });
  if (payload.ok) {
    return bodyApi;
  } else {
    throw new ErrorAPI(
      payload.status,
      ...bodyApi.errors.reduce((prev: any, curr: any) => prev + ` ${curr}`, ""),
    );
  }
};

export const approveContractApi = async ({
  agencyId,
  contractId,
  phoneNumber,
  email,
}: {
  agencyId: string;
  contractId: string;
  phoneNumber: string;
  email: string;
}) => {
  const { payload, body: bodyApi } = await customSecuredApi.call({
    method: "POST",
    path: `${Config.apis.products.approveContract
      .replace(":agencyId", agencyId)
      .replace(":contractId", contractId)}`,
    body: { phoneNumber, email },
  });
  if (payload.ok) {
    return bodyApi;
  }
  throw new ErrorAPI(
    payload.status,
    ...bodyApi.errors.reduce((prev: any, curr: any) => prev + ` ${curr}`, ""),
  );
};

export const getImageModel = async (product: Product) => {
  if (product.hasS3Data === true) {
    return customSecuredApi
    .call({
      method: "GET",
      path: `${Config.apis.icecat.internal}?pn=${product.partNumber}`,
    })
    .then((res) => {
      console.log('getImageModel', res);
      return res.body.Data;
    });
  }
  return customSecuredApi
    .call({
      method: "GET",
      path: `${Config.apis.icecat.content}?url=${encodeURIComponent(
        Config.apis.icecat.completeInfo.replace(
          ":codiceGTIN",
          "&GTIN=" + product.ean,
        ),
      )}`,
    })
    .then(
      (res) => {
        return res.body.data;
      },
      (r) => {
        if (product.iceCatId) {
          return customSecuredApi
            .call({
              method: "GET",
              path: `${Config.apis.icecat.content}?url=${encodeURIComponent(
                Config.apis.icecat.completeInfo.replace(":codiceGTIN", "") +
                  "&icecat_id=" +
                  product.iceCatId,
              )}`,
            })
            .then((res) => {
              return res.body.data;
            });
        }
        return r;
      },
    );
};

export const GetImageUrl = (fromS3?: boolean, url?: string): string => {
  if (fromS3) return url ?? '';
  if (!url) return '';
  var l = `${Config.apiBaseurl}${
    Config.apis.icecat.gallery
  }?url=${encodeURIComponent(url)}`;
  return l;
};


export function isProductValidForVirtualLand (x: ProductForFe) {
  // console.log('isProductValidForVirtualLand', x.familyId);
  // return x.familyId === 'iphone' || x.familyId === 'sam_smartphones';
  return false;
}

export const GetVirtualLandStories = () => 
  customSecuredApi.call({
    method: "GET",
    path: `${Config.apis.virtualLand.stories}`,
  });